import type { Dispatch } from "react";
import type { RegionUpdateTabFragment } from "../client/generated";
import type { EditorAction, EditorState } from "../editor/reducers";
import { useTranslation } from "react-i18next";
import { NavbarTabContent } from "../shell/NavbarTabContent";
import { ScrollArea } from "@acdc2/ui/components/scroll-area";
import { NavbarTabCloseButton } from "../shell/NavbarTabCloseButton";
import { getRegionName, matchRegionType } from "./utils";
import { makeTabIdentifier } from "../editor/reducers";
import { ImageRegionEditor } from "./image/ImageRegionEditor";
import { HybridRegionEditor } from "./hybrid/HybridRegionEditor";
import { HeadingRegionEditor } from "./heading/HeadingRegionEditor";
import { TextRegionEditor } from "./text/TextRegionEditor";
import { CaptionRegionEditor } from "./caption/CaptionRegionEditor";
import { FormulaRegionEditor } from "./formula/FormulaRegionEditor";
import { FigureRegionEditor } from "./figure/FigureRegionEditor";
import { ListRegionEditor } from "./list/ListRegionEditor";
import { ArtifactRegionEditor } from "./artifact/ArtifactRegionEditor";
import { TableRegionEditor } from "./table/TableRegionEditor";
import { TOCRegionEditor } from "./toc/TOCRegionEditor";
import { ListItemRegionEditor } from "./list-item/ListItemRegionEditor";
import { SublistRegionEditor } from "./sublist/SublistRegionEditor";
import { UntypedRegionEditor } from "./untyped/UntypedRegionEditor";
import { SidebarDivider } from "../shell/SidebarDivider";
import { RegionIssueAlerts } from "../issues/RegionIssueAlerts";

type Props = {
  fragment: RegionUpdateTabFragment;
  state: EditorState;
  dispatch: Dispatch<EditorAction>;
};

export function RegionUpdateTab({ fragment, state, dispatch }: Props) {
  const { t } = useTranslation();
  const regionName = getRegionName(fragment);

  return (
    <NavbarTabContent
      value={makeTabIdentifier("update", fragment.id)}
      title={t(`RegionUpdateTab.title`, { regionName })}
      right={<NavbarTabCloseButton dispatch={dispatch} />}
    >
      <ScrollArea className="flex-1">
        {fragment.issues.length > 0 && (
          <>
            <RegionIssueAlerts fragment={fragment} />
            <SidebarDivider />
          </>
        )}
        <Body fragment={fragment} state={state} dispatch={dispatch} />
      </ScrollArea>
    </NavbarTabContent>
  );
}

function Body({ fragment, state, dispatch }: Props) {
  const [regionType, params] = matchRegionType(fragment);

  switch (regionType) {
    case "artifact":
      return (
        <ArtifactRegionEditor
          paginationArtifactAttributeId={params["paginationId"]}
          dispatch={dispatch}
        />
      );
    case "caption":
      return (
        <CaptionRegionEditor
          captionAttributeId={params["captionId"]}
          textAttributeId={params["textId"]}
          dispatch={dispatch}
        />
      );
    case "figure":
      return (
        <FigureRegionEditor
          figureAttributeId={params["figureId"]}
          dispatch={dispatch}
        />
      );
    case "formula":
      return (
        <FormulaRegionEditor
          expressionAttributeId={params["expressionId"]}
          dispatch={dispatch}
        />
      );
    case "heading":
      return (
        <HeadingRegionEditor
          headerLevelAttributeId={params["headerLevelId"]}
          textAttributeId={params["textId"]}
          dispatch={dispatch}
        />
      );
    case "image":
      return (
        <ImageRegionEditor
          alternativeTextAttributeId={params["alternativeTextId"]}
          imageAttributeId={params["imageId"]}
          dispatch={dispatch}
        />
      );
    case "list":
      return (
        <ListRegionEditor
          listAttributeId={params["listId"]}
          dispatch={dispatch}
        />
      );
    case "listItem":
      return (
        <ListItemRegionEditor
          listItemAttributeId={params["listItemId"]}
          textAttributeId={params["textId"]}
          dispatch={dispatch}
        />
      );
    case "sublist":
      return (
        <SublistRegionEditor
          listAttributeId={params["listId"]}
          listItemAttributeId={params["listItemId"]}
          dispatch={dispatch}
        />
      );
    case "table":
      return (
        <TableRegionEditor
          tableAttributeId={params["tableId"]}
          state={state}
          dispatch={dispatch}
        />
      );
    case "toc":
      return (
        <TOCRegionEditor tocAttributeId={params["tocId"]} dispatch={dispatch} />
      );
    case "text":
      return (
        <TextRegionEditor
          textAttributeId={params["textId"]}
          dispatch={dispatch}
        />
      );
    case "untyped":
      return <UntypedRegionEditor regionId={fragment.id} dispatch={dispatch} />;
    default:
      return <HybridRegionEditor regionId={fragment.id} />;
  }
}
